import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFormat } from "../i18n/format";

interface Props {
  online: number;
  total: number;
}

/**
 * Hand-drawn SVG donut - no chart-library dependency for one chart.
 * Renders a green arc proportional to online / total, with the % in
 * the centre. Mirrors the Nenda admin look from the screenshot.
 */
export default function OnlineScreensDonut({ online, total }: Props) {
  const { t } = useTranslation("analytics");
  const fmt = useFormat();
  const pct = total === 0 ? 0 : (online / total) * 100;
  const size = 160;
  const stroke = 24;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = (pct / 100) * circumference;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      <Typography variant="overline" color="text.secondary">
        {t("onlineScreensDonut.title")}
      </Typography>
      <Box sx={{ position: "relative", width: size, height: size }}>
        <svg
          width={size}
          height={size}
          aria-label={t("onlineScreensDonut.ariaLabel")}
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="rgba(0,0,0,0.08)"
            strokeWidth={stroke}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#6eae85"
            strokeWidth={stroke}
            strokeDasharray={`${dash} ${circumference}`}
            strokeLinecap="butt"
            // Start at 12 o'clock and proceed clockwise.
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        </svg>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: "fontWeightSemibold",
              lineHeight: 1,
              color: "#2e7d50",
            }}
          >
            {fmt.number(pct, { maximumFractionDigits: 0 })}%
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {fmt.number(online)} / {fmt.number(total)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
